import fetchWithAuthRetry from '../utils/fetchWithAuthRetry';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.adjusterphoto.com';

// Types
export interface BillingProduct {
  product_type: string;
  tier_id: string;
  display_name: string;
  monthly_price: string;
  annual_price: string;
  monthly_price_id: string;
  annual_price_id: string;
  features: string[];
}

export interface CreditPack {
  credits: number;
  price: string;
  price_id: string;
  savings?: string;
}

export interface BillingConfigResponse {
  publishable_key: string;
  products: BillingProduct[];
  credit_packs: CreditPack[];
}

export interface SubscriptionInfo {
  id: string;
  product_type: string;
  tier_id: string;
  status: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  stripe_price_id: string;
}

export interface SubscriptionResponse {
  subscriptions: SubscriptionInfo[];
}

export interface CreditTransaction {
  id: number;
  amount: number;
  balance_after: number;
  transaction_type: string;
  description?: string;
  created_at: string;
}

export interface CreditsResponse {
  balance: number;
  transactions: CreditTransaction[];
}

// Get billing configuration (publishable key, products, prices)
export async function getBillingConfig(): Promise<BillingConfigResponse> {
  const response = await fetch(`${API_URL}/api/billing/config`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch billing config');
  }

  return response.json();
}

// Create a Stripe Checkout session and get the redirect URL
export async function createCheckoutSession(
  priceId: string,
  mode: 'subscription' | 'payment'
): Promise<string> {
  const response = await fetchWithAuthRetry(`${API_URL}/api/billing/create-checkout-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ price_id: priceId, mode }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to create checkout session');
  }

  const data = await response.json();
  return data.url;
}

// Create a Stripe Customer Portal session and get the redirect URL
export async function createPortalSession(): Promise<string> {
  const response = await fetchWithAuthRetry(`${API_URL}/api/billing/create-portal-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to create portal session');
  }

  const data = await response.json();
  return data.url;
}

// Get user's current subscriptions
export async function getSubscription(): Promise<SubscriptionResponse> {
  const response = await fetchWithAuthRetry(`${API_URL}/api/billing/subscription`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch subscription');
  }

  return response.json();
}

// Get user's AI credit balance and transaction history
export async function getCredits(): Promise<CreditsResponse> {
  const response = await fetchWithAuthRetry(`${API_URL}/api/billing/credits`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch credits');
  }

  return response.json();
}
